/**
 * Bill Review Modal
 * Allows users to review and modify parsed bill data before creating an expense
 */

import React, { useEffect, useState, useMemo } from 'react';
import { X, AlertCircle, CheckCircle, Info, FileText, Receipt } from 'lucide-react';
import type { MatchedBillData, ParsedLineItem } from '../types/billParser';
import { convertToFormValues, formatDateForForm } from '../services/billParserService';
import { calculateAmount } from '../utils/calculateAmount';
import { useAuthContext } from '../context/AuthContext';
import { useTitleCase } from '../hooks/useTitleCase';
import GroupedCategoryDropdown from './forms/GroupedCategoryDropdown';
import Button from '../ui/Button';

type TypeOption = { id: string; name: string };
type UnitOption = { id: string; name: string; type: string };
type CostCenterOption = { id: string; name: string; type: string };
type CategoryOption = {
  id: string;
  name: string;
  type: string;
  costCenter: string;
  needsNameField?: boolean;
  supportsUnits?: boolean;
};
type PartyOption = { id: string; name: string; type: string };

interface BillReviewModalProps {
  open: boolean;
  matchedData: MatchedBillData | null;
  billFile: File | null;
  onClose: () => void;
  onSubmit: (formData: any, keepAttachment: boolean, file: File | null) => Promise<void>;
  types: TypeOption[];
  units: UnitOption[];
  costCenters: CostCenterOption[];
  categories: CategoryOption[];
  parties: PartyOption[];
}

const BillReviewModal: React.FC<BillReviewModalProps> = ({
  open,
  matchedData,
  billFile,
  onClose,
  onSubmit,
  types,
  units,
  costCenters,
  categories,
  parties,
}) => {
  const { roles, assignedUnits } = useAuthContext();
  const toTitleCase = useTitleCase();

  // Form state
  const [formData, setFormData] = useState({
    type: '',
    unit: '',
    costCenter: '',
    category: '',
    partyName: '',
    partyType: 'vendor' as 'shop' | 'vendor' | 'office' | 'labourGroup',
    date: new Date().toISOString().split('T')[0],
    mode: 'total' as 'total' | 'unit',
    gross: '',
    quantity: '',
    rate: '',
    gstAmount: '',
    enableGst: false,
    amount: '',
    description: '',
    invoiceNo: '',
    nature: 'routine',
    keepAttachment: true,
  });

  // UI state
  const [showUnits, setShowUnits] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Preview URL for the bill
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Initialize form when matchedData changes
  useEffect(() => {
    if (!open || !matchedData) return;

    const initialValues = convertToFormValues(matchedData);

    setFormData({
      type: initialValues.type || '',
      unit: initialValues.unit || '',
      costCenter: initialValues.costCenter || '',
      category: initialValues.category || '',
      partyName: initialValues.partyName || '',
      partyType: initialValues.partyType || 'vendor',
      date: initialValues.date || new Date().toISOString().split('T')[0],
      mode: initialValues.mode || 'total',
      gross: initialValues.gross || '',
      quantity: '',
      rate: '',
      gstAmount: initialValues.gstAmount || '0',
      enableGst: initialValues.enableGst || false,
      amount: initialValues.amount || '',
      description: initialValues.description || '',
      invoiceNo: initialValues.invoiceNo || '',
      nature: initialValues.nature || 'routine',
      keepAttachment: true,
    });

    setError(null);
    setErrors({});

    // Create preview URL for images
    if (billFile && billFile.type.startsWith('image/')) {
      const url = URL.createObjectURL(billFile);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setPreviewUrl(null);
    }
  }, [matchedData, open, billFile]);

  // Auto-calculate amount
  useEffect(() => {
    const computed = calculateAmount({
      mode: formData.mode,
      quantity: formData.quantity,
      rate: formData.rate,
      gross: formData.gross,
      gstAmount: formData.enableGst ? formData.gstAmount : '0',
      enableGst: formData.enableGst,
      supportsUnits: showUnits,
    });
    setFormData((prev) => ({ ...prev, amount: computed ? String(computed) : '' }));
  }, [
    formData.mode,
    formData.quantity,
    formData.rate,
    formData.gross,
    formData.gstAmount,
    formData.enableGst,
    showUnits,
  ]);

  // Update showUnits when category changes
  useEffect(() => {
    const cat = categories.find((c) => c.id === formData.category);
    setShowUnits(!!cat?.supportsUnits);
  }, [formData.category, categories]);

  // Filtered dropdowns based on RBAC
  const filteredUnits = useMemo(() => {
    return units.filter((u) => {
      const matchesType = !formData.type || u.type === formData.type;
      const hasAccess = roles.includes('admin') || assignedUnits.includes(u.id);
      return matchesType && hasAccess;
    });
  }, [units, formData.type, roles, assignedUnits]);

  const filteredCostCenters = useMemo(() => {
    return costCenters.filter((c) => !formData.type || c.type === formData.type);
  }, [costCenters, formData.type]);

  // Validation
  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.type) newErrors.type = 'Type is required';
    if (!formData.unit) newErrors.unit = 'Unit is required';
    if (!formData.costCenter) newErrors.costCenter = 'Cost Center is required';
    if (!formData.category) newErrors.category = 'Category is required';
    if (!formData.partyName) newErrors.partyName = 'Vendor/Shop is required';
    if (!formData.date) newErrors.date = 'Date is required';

    // Date validation
    if (formData.date && new Date(formData.date) > new Date()) {
      newErrors.date = 'Date cannot be in the future';
    }

    // Amount validation
    if (formData.mode === 'total') {
      if (!formData.gross) newErrors.gross = 'Gross amount is required';
    } else {
      if (!formData.quantity) newErrors.quantity = 'Quantity is required';
      if (!formData.rate) newErrors.rate = 'Rate is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit handler
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setError(null);
    setLoading(true);

    try {
      const amount = calculateAmount({
        mode: formData.mode,
        quantity: formData.quantity,
        rate: formData.rate,
        gross: formData.gross,
        gstAmount: formData.enableGst ? formData.gstAmount : '0',
        enableGst: formData.enableGst,
        supportsUnits: showUnits,
      });

      const submitData = {
        typeId: formData.type,
        type: types.find((t) => t.id === formData.type)?.name || '',
        unitId: formData.unit,
        unit: units.find((u) => u.id === formData.unit)?.name || '',
        costCenterId: formData.costCenter,
        costCenter: costCenters.find((c) => c.id === formData.costCenter)?.name || '',
        categoryId: formData.category,
        category: categories.find((c) => c.id === formData.category)?.name || '',
        partyName: toTitleCase(formData.partyName),
        partyType: formData.partyType,
        shop: toTitleCase(formData.partyName), // Legacy field
        amount,
        ...(formData.mode === 'total'
          ? { grossAmount: Number(formData.gross || 0), mode: 'total' }
          : {
              quantity: Number(formData.quantity || 0),
              rate: Number(formData.rate || 0),
              mode: 'unit',
            }),
        ...(formData.enableGst ? { gstAmount: Number(formData.gstAmount) } : { gstAmount: 0 }),
        date: formData.date,
        description: toTitleCase(formData.description || ''),
        invoiceNo: formData.invoiceNo || '',
        nature: formData.nature,
      };

      await onSubmit(submitData, formData.keepAttachment, billFile);
      onClose();
    } catch (err: any) {
      console.error('Error creating expense from bill:', err);
      setError(err.message || 'Error creating expense');
    }
    setLoading(false);
  };

  if (!open || !matchedData) return null;

  const parsedData = matchedData.parsedData;
  const todayStr = new Date().toISOString().split('T')[0];
  const minDateStr = roles.includes('admin')
    ? ''
    : new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white w-full max-w-6xl rounded-xl shadow-2xl max-h-[90vh] overflow-hidden flex flex-col animate-in fade-in slide-in-from-bottom-4 duration-200">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Receipt className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Review Parsed Bill</h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  Verify and adjust the extracted data before creating expense
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close modal"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content - Two Column Layout */}
          <div className="flex-1 overflow-y-auto">
            <div className="flex flex-col lg:flex-row">
              {/* Left Column: Parsed Bill Info */}
              <div className="lg:w-2/5 p-6 bg-gray-50 border-r border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Extracted from Bill
                </h3>

                {/* Bill Preview */}
                {previewUrl && (
                  <div className="mb-4">
                    <img
                      src={previewUrl}
                      alt="Bill preview"
                      className="max-h-48 w-full object-contain rounded-lg border bg-white"
                    />
                  </div>
                )}

                {billFile && (
                  <div className="mb-4 p-3 bg-white rounded-lg border">
                    <div className="text-sm font-medium text-gray-700 truncate">
                      {billFile.name}
                    </div>
                    <div className="text-xs text-gray-500">
                      {(billFile.size / 1024).toFixed(1)} KB
                    </div>
                  </div>
                )}

                {/* Parsed Data Summary */}
                <div className="space-y-3">
                  {parsedData.vendorName && (
                    <div className="p-3 bg-white rounded-lg border">
                      <div className="text-xs text-gray-500 uppercase tracking-wide">Vendor</div>
                      <div className="text-sm font-medium text-gray-900 mt-1">
                        {parsedData.vendorName}
                      </div>
                      {parsedData.vendorGST && (
                        <div className="text-xs text-gray-500 mt-1">
                          GST: {parsedData.vendorGST}
                        </div>
                      )}
                    </div>
                  )}

                  {parsedData.invoiceNumber && (
                    <div className="p-3 bg-white rounded-lg border">
                      <div className="text-xs text-gray-500 uppercase tracking-wide">Invoice #</div>
                      <div className="text-sm font-medium text-gray-900 mt-1">
                        {parsedData.invoiceNumber}
                      </div>
                    </div>
                  )}

                  {parsedData.invoiceDate && (
                    <div className="p-3 bg-white rounded-lg border">
                      <div className="text-xs text-gray-500 uppercase tracking-wide">Date</div>
                      <div className="text-sm font-medium text-gray-900 mt-1">
                        {parsedData.invoiceDate}
                      </div>
                    </div>
                  )}

                  {/* Line Items */}
                  {parsedData.lineItems.length > 0 && (
                    <div className="p-3 bg-white rounded-lg border">
                      <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">
                        Items ({parsedData.lineItems.length})
                      </div>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {parsedData.lineItems.map((item, idx) => (
                          <div
                            key={idx}
                            className="text-sm flex justify-between items-start py-1 border-b border-gray-100 last:border-0"
                          >
                            <div className="flex-1 pr-2">
                              <div className="text-gray-700">{item.description}</div>
                              {item.quantity && (
                                <div className="text-xs text-gray-500">
                                  {item.quantity} {item.unit || 'units'} @ {item.rate || '-'}
                                </div>
                              )}
                            </div>
                            <div className="font-medium text-gray-900">
                              {item.amount.toLocaleString('en-IN')}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Totals */}
                  <div className="p-3 bg-primary/5 rounded-lg border border-primary/20">
                    <div className="space-y-1">
                      {parsedData.subtotal && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Subtotal</span>
                          <span>{parsedData.subtotal.toLocaleString('en-IN')}</span>
                        </div>
                      )}
                      {(parsedData.gstAmount || parsedData.taxAmount) && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">
                            Tax/GST {parsedData.gstPercent ? `(${parsedData.gstPercent}%)` : ''}
                          </span>
                          <span>
                            {(parsedData.gstAmount || parsedData.taxAmount)?.toLocaleString(
                              'en-IN'
                            )}
                          </span>
                        </div>
                      )}
                      {parsedData.discount && (
                        <div className="flex justify-between text-sm text-success">
                          <span>Discount</span>
                          <span>-{parsedData.discount.toLocaleString('en-IN')}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-base font-semibold pt-2 border-t border-primary/20">
                        <span>Total</span>
                        <span className="text-primary">
                          {parsedData.totalAmount?.toLocaleString('en-IN') || '-'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Confidence Indicator */}
                  {parsedData.confidence !== undefined && (
                    <div className="p-3 bg-white rounded-lg border">
                      <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                        Parse Confidence
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${
                              parsedData.confidence > 0.7
                                ? 'bg-success'
                                : parsedData.confidence > 0.4
                                ? 'bg-warning'
                                : 'bg-error'
                            }`}
                            style={{ width: `${parsedData.confidence * 100}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium">
                          {Math.round(parsedData.confidence * 100)}%
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Column: Expense Form */}
              <div className="lg:w-3/5 p-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-4">
                  Create Expense Entry
                </h3>

                <div className="space-y-6">
                  {/* Error Alert */}
                  {error && (
                    <div className="p-4 bg-error-50 border border-error-200 rounded-lg flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-error-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-error-800">Error</p>
                        <p className="text-sm text-error-600 mt-1">{error}</p>
                      </div>
                    </div>
                  )}

                  {/* Unmatched Suggestions Warning */}
                  {matchedData.unmatchedSuggestions.length > 0 && (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
                      <Info className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-amber-800">
                          Some fields need manual selection
                        </p>
                        <p className="text-sm text-amber-600 mt-1">
                          We couldn't automatically match:{' '}
                          {matchedData.unmatchedSuggestions.map((s) => s.field).join(', ')}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Section 1: Expense Hierarchy */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-medium text-gray-700 pb-2 border-b flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">
                        1
                      </div>
                      Expense Hierarchy
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Type <span className="text-error-500">*</span>
                        </label>
                        <select
                          value={formData.type}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              type: e.target.value,
                              unit: '',
                              costCenter: '',
                              category: '',
                            })
                          }
                          className={`w-full px-3 py-2.5 bg-white border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors ${
                            errors.type ? 'border-error-500' : 'border-gray-300'
                          }`}
                        >
                          <option value="">Select Type</option>
                          {types.map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.name}
                            </option>
                          ))}
                        </select>
                        {errors.type && (
                          <p className="text-xs text-error-600 mt-1">{errors.type}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Unit <span className="text-error-500">*</span>
                        </label>
                        <select
                          value={formData.unit}
                          onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                          disabled={!formData.type}
                          className={`w-full px-3 py-2.5 bg-white border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed ${
                            errors.unit ? 'border-error-500' : 'border-gray-300'
                          }`}
                        >
                          <option value="">Select Unit</option>
                          {filteredUnits.map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.name}
                            </option>
                          ))}
                        </select>
                        {errors.unit && (
                          <p className="text-xs text-error-600 mt-1">{errors.unit}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Cost Center <span className="text-error-500">*</span>
                        </label>
                        <select
                          value={formData.costCenter}
                          onChange={(e) =>
                            setFormData({ ...formData, costCenter: e.target.value, category: '' })
                          }
                          disabled={!formData.type}
                          className={`w-full px-3 py-2.5 bg-white border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed ${
                            errors.costCenter ? 'border-error-500' : 'border-gray-300'
                          }`}
                        >
                          <option value="">Select Cost Center</option>
                          {filteredCostCenters.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                        {errors.costCenter && (
                          <p className="text-xs text-error-600 mt-1">{errors.costCenter}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Category <span className="text-error-500">*</span>
                        </label>
                        <GroupedCategoryDropdown
                          typeId={formData.type}
                          costCenterId={formData.costCenter}
                          nature={formData.nature}
                          fallbackNature={formData.nature || 'routine'}
                          value={formData.category}
                          onSelect={(categoryId) =>
                            setFormData({ ...formData, category: categoryId })
                          }
                          placeholder="Select category..."
                          disabled={!formData.costCenter}
                        />
                        {errors.category && (
                          <p className="text-xs text-error-600 mt-1">{errors.category}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Section 2: Transaction Details */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-medium text-gray-700 pb-2 border-b flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full bg-success/10 text-success flex items-center justify-center text-xs font-bold">
                        2
                      </div>
                      Transaction Details
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Vendor/Shop <span className="text-error-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.partyName}
                          onChange={(e) =>
                            setFormData({ ...formData, partyName: e.target.value })
                          }
                          placeholder="Vendor name"
                          className={`w-full px-3 py-2.5 bg-white border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors ${
                            errors.partyName ? 'border-error-500' : 'border-gray-300'
                          }`}
                        />
                        {errors.partyName && (
                          <p className="text-xs text-error-600 mt-1">{errors.partyName}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Date <span className="text-error-500">*</span>
                        </label>
                        <input
                          type="date"
                          value={formData.date}
                          onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                          max={todayStr}
                          min={minDateStr}
                          className={`w-full px-3 py-2.5 bg-white border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors ${
                            errors.date ? 'border-error-500' : 'border-gray-300'
                          }`}
                        />
                        {errors.date && (
                          <p className="text-xs text-error-600 mt-1">{errors.date}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Invoice Number
                        </label>
                        <input
                          type="text"
                          value={formData.invoiceNo}
                          onChange={(e) => setFormData({ ...formData, invoiceNo: e.target.value })}
                          placeholder="Invoice #"
                          className="w-full px-3 py-2.5 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Nature
                        </label>
                        <select
                          value={formData.nature}
                          onChange={(e) => setFormData({ ...formData, nature: e.target.value })}
                          className="w-full px-3 py-2.5 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                        >
                          <option value="routine">Routine</option>
                          <option value="stock">Stock</option>
                          <option value="payroll">Payroll</option>
                          <option value="admin-finance">Admin & Finance</option>
                          <option value="capital">Capital</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Section 3: Amount */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-medium text-gray-700 pb-2 border-b flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full bg-info/10 text-info flex items-center justify-center text-xs font-bold">
                        3
                      </div>
                      Amount Details
                    </h4>

                    {/* Mode Toggle */}
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm font-medium text-gray-700">Mode:</span>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, mode: 'total' })}
                          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                            formData.mode === 'total'
                              ? 'bg-primary text-white'
                              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          Total Amount
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, mode: 'unit' })}
                          disabled={!showUnits}
                          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                            formData.mode === 'unit'
                              ? 'bg-primary text-white'
                              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          Qty x Rate
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {formData.mode === 'total' ? (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Gross Amount <span className="text-error-500">*</span>
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={formData.gross}
                            onChange={(e) => setFormData({ ...formData, gross: e.target.value })}
                            placeholder="0.00"
                            className={`w-full px-3 py-2.5 bg-white border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors ${
                              errors.gross ? 'border-error-500' : 'border-gray-300'
                            }`}
                          />
                          {errors.gross && (
                            <p className="text-xs text-error-600 mt-1">{errors.gross}</p>
                          )}
                        </div>
                      ) : (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                              Quantity <span className="text-error-500">*</span>
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              value={formData.quantity}
                              onChange={(e) =>
                                setFormData({ ...formData, quantity: e.target.value })
                              }
                              placeholder="0"
                              className={`w-full px-3 py-2.5 bg-white border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors ${
                                errors.quantity ? 'border-error-500' : 'border-gray-300'
                              }`}
                            />
                            {errors.quantity && (
                              <p className="text-xs text-error-600 mt-1">{errors.quantity}</p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                              Rate <span className="text-error-500">*</span>
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              value={formData.rate}
                              onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                              placeholder="0.00"
                              className={`w-full px-3 py-2.5 bg-white border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors ${
                                errors.rate ? 'border-error-500' : 'border-gray-300'
                              }`}
                            />
                            {errors.rate && (
                              <p className="text-xs text-error-600 mt-1">{errors.rate}</p>
                            )}
                          </div>
                        </>
                      )}

                      {/* GST */}
                      <div className="md:col-span-2 space-y-3">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.enableGst}
                            onChange={(e) =>
                              setFormData({ ...formData, enableGst: e.target.checked })
                            }
                            className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                          />
                          <span className="text-sm font-medium text-gray-700">
                            Include GST/Tax Amount
                          </span>
                        </label>

                        {formData.enableGst && (
                          <input
                            type="number"
                            step="0.01"
                            value={formData.gstAmount}
                            onChange={(e) =>
                              setFormData({ ...formData, gstAmount: e.target.value })
                            }
                            placeholder="GST Amount"
                            className="w-full md:w-1/2 px-3 py-2.5 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                          />
                        )}
                      </div>
                    </div>

                    {/* Final Amount Display */}
                    <div className="p-4 bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/30 rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">Final Amount</span>
                        <span className="text-2xl font-semibold text-primary">
                          {parseFloat(formData.amount || '0').toLocaleString('en-IN', {
                            minimumFractionDigits: 2,
                          })}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Section 4: Additional */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-medium text-gray-700 pb-2 border-b flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full bg-secondary/10 text-secondary flex items-center justify-center text-xs font-bold">
                        4
                      </div>
                      Additional Details
                    </h4>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Description/Notes
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Add notes or description..."
                        rows={2}
                        className="w-full px-3 py-2.5 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors resize-none"
                      />
                    </div>

                    {/* Keep Attachment Option */}
                    <label className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.keepAttachment}
                        onChange={(e) =>
                          setFormData({ ...formData, keepAttachment: e.target.checked })
                        }
                        className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                      />
                      <span className="text-sm text-gray-700">
                        Attach bill image to this expense
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-3 sticky bottom-0">
            <button
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={loading}
              className="flex items-center gap-2 min-w-[140px] justify-center"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Creating...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Create Expense
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default BillReviewModal;
